import { useCallback, useEffect, useRef, useState } from "react";
import { Howl, Howler } from "howler";
import { SoundsInfo } from "../Lists/SoundInfo";
import useSoundStore from "../store/useSoundStore";

const DEFAULT_VOLUME = 0.5;

export default function useSoundMixer() {
  const [masterVolume, updateMasterVolume] = useState(1);
  const audioRef = useRef([]);
  const previousMasterVolume = useRef(null);
  const [sounds, setSounds] = useState([]);
  const [volumes, setVolumes] = useState(() => SoundsInfo.map(() => DEFAULT_VOLUME));
  const [isPaused, setIsPaused] = useState(false);
  const playbackIds = useRef(new Map());
  const outgoingSounds = useRef(new Map());
  const stopOutgoing = useCallback(() => {
    outgoingSounds.current.forEach(({ sound, id, timeout }) => {
      clearTimeout(timeout);
      sound.stop(id);
    });
    outgoingSounds.current.clear();
  }, []);
  const playingSounds = useSoundStore((state) => state.playingSounds);
  const setPlayingSounds = useSoundStore((state) => state.setPlayingSounds);
  const toggleResetSignal = useSoundStore((state) => state.toggleResetSignal);

  useEffect(() => {
    if (playingSounds.length === 0) setIsPaused(false);
  }, [playingSounds.length]);

  const ensureAudio = () => {
    if (previousMasterVolume.current === null) previousMasterVolume.current = Howler.volume();
    if (audioRef.current.length === 0) {
      // Creating the audio context is deferred until a listener uses the mixer.
      audioRef.current = SoundsInfo.map((item) => new Howl({
        src: [item.Sound], volume: DEFAULT_VOLUME, loop: true, preload: false,
      }));
      Howler.volume(masterVolume);
      setSounds(audioRef.current);
    }
    return audioRef.current;
  };

  const setMasterVolume = (volume) => {
    if (previousMasterVolume.current === null) previousMasterVolume.current = Howler.volume();
    Howler.volume(volume);
    updateMasterVolume(volume);
  };

  useEffect(() => {
    const ids = playbackIds.current;
    return () => {
      stopOutgoing();
      audioRef.current.forEach((sound) => sound.unload());
      audioRef.current = [];
      ids.clear();
      if (previousMasterVolume.current !== null) {
        Howler.volume(previousMasterVolume.current);
        previousMasterVolume.current = null;
      }
      setPlayingSounds([]);
    };
  }, [setPlayingSounds, stopOutgoing]);

  const setVolume = useCallback((sound, volume, index) => {
    sound?.volume(volume);
    setVolumes((previous) => previous.map((value, i) => i === index ? volume : value));
  }, []);

  const pause = useCallback(() => {
    stopOutgoing();
    playingSounds.forEach((index) => sounds[index]?.pause(playbackIds.current.get(index)));
    setIsPaused(true);
  }, [sounds, playingSounds, stopOutgoing]);

  const playSound = (index) => {
    const sound = audioRef.current[index];
    if (!sound) return;
    if (sound.state() === "unloaded") sound.load();
    const id = playbackIds.current.get(index);
    if (!sound.playing(id)) playbackIds.current.set(index, sound.play(id));
  };

  const resume = () => {
    playingSounds.forEach(playSound);
    setIsPaused(false);
  };

  const togglePlay = (index) => {
    stopOutgoing();
    const sound = ensureAudio()[index];
    if (!sound) return;
    if (playingSounds.includes(index)) {
      sound.stop(playbackIds.current.get(index));
      playbackIds.current.delete(index);
      setPlayingSounds((previous) => previous.filter((i) => i !== index));
      setVolume(sound, DEFAULT_VOLUME, index);
    } else {
      sound.volume(volumes[index]);
      if (!isPaused) playSound(index);
      setPlayingSounds((previous) => [...previous, index]);
    }
  };

  const playMix = (mix, { fadeDuration = 0 } = {}) => {
    stopOutgoing();
    const sounds = ensureAudio();
    const selected = mix.filter(({ index }) => sounds[index]);
    if (fadeDuration > 0) {
      const selectedIndices = new Set(selected.map(({ index }) => index));
      playingSounds.forEach((index) => {
        if (selectedIndices.has(index)) return;
        const sound = sounds[index];
        const id = playbackIds.current.get(index);
        playbackIds.current.delete(index);
        if (!sound) return;
        if (isPaused) { sound.stop(id); return; }
        sound.fade(sound.volume(), 0, fadeDuration, id);
        const timeout = setTimeout(() => {
          sound.stop(id);
          outgoingSounds.current.delete(index);
        }, fadeDuration);
        outgoingSounds.current.set(index, { sound, id, timeout });
      });
      selected.forEach(({ index, volume = DEFAULT_VOLUME }) => {
        const sound = sounds[index];
        const from = playingSounds.includes(index) ? sound.volume() : 0;
        setVolumes((previous) => previous.map((value, i) => i === index ? volume : value));
        sound.volume(from);
        playSound(index);
        sound.fade(from, volume, fadeDuration, playbackIds.current.get(index));
      });
      setPlayingSounds(selected.map(({ index }) => index));
      setIsPaused(false);
      return;
    }
    // Stop paused instances too, so replaying a mix cannot duplicate its audio.
    playingSounds.forEach((index) => sounds[index]?.stop(playbackIds.current.get(index)));
    playbackIds.current.clear();
    selected.forEach(({ index, volume }) => {
      setVolume(sounds[index], volume ?? DEFAULT_VOLUME, index);
      playSound(index);
    });
    setPlayingSounds(selected.map(({ index }) => index));
    setIsPaused(false);
  };

  const clear = () => {
    stopOutgoing();
    playingSounds.forEach((index) => sounds[index]?.stop(playbackIds.current.get(index)));
    playbackIds.current.clear();
    sounds.forEach((sound) => {
      sound.volume(DEFAULT_VOLUME);
    });
    setPlayingSounds([]);
    setVolumes(SoundsInfo.map(() => DEFAULT_VOLUME));
    setIsPaused(false);
    toggleResetSignal();
  };

  return { masterVolume, setMasterVolume, sounds, volumes, playingSounds, isPaused, setVolume, togglePlay, playMix, pause, resume, clear };
}
